import React, { useState, useEffect, useCallback, useId } from 'react';
import { api } from '../api';
import Select from './Select';
import InsumoAutocomplete, { ORIGEM_DIGITACAO } from './InsumoAutocomplete';
import {
  formatarProducao, labelPorArea, labelPorUnidade, labelUnidadePlural,
} from '../utils/unidades';
import Metrica, { MetricaGrid } from './ui/Metrica';
import DataTable from './ui/DataTable';
import DonutChart from './ui/DonutChart';
import Modal from './ui/Modal';
import Field, { FormRow } from './ui/Field';
import Estado from './ui/Estado';
import Icon from './ui/Icon';
import { fmt, fmtBRL, hoje } from '../utils/format';
import { CATEGORIAS, labelCategoria, categoriaPadraoPara } from '../utils/categorias';

const TIPOS_CUSTO = [
  { value: 'PLANTIO',      label: 'Plantio' },
  { value: 'ADUBACAO',     label: 'Adubação' },
  { value: 'DEFENSIVO',    label: 'Defensivo' },
  { value: 'PULVERIZACAO', label: 'Pulverização' },
  { value: 'IRRIGACAO',    label: 'Irrigação' },
  { value: 'ARRENDAMENTO', label: 'Arrendamento' },
  { value: 'MAO_DE_OBRA',  label: 'Mão de obra' },
  { value: 'COMBUSTIVEL',  label: 'Combustível' },
  { value: 'FINANCEIRO',   label: 'Financeiro' },
  { value: 'SERVICO',      label: 'Serviço' },
  { value: 'OUTRO',        label: 'Outro' },
];

// ── Preview FIFO inline por linha ─────────────────────────────────────────────
function FifoPreview({ lotes, quantidade, unidade }) {
  const simulacao = [];
  let restante = quantidade;
  for (const lote of lotes) {
    if (restante <= 0) break;
    const usar = Math.min(lote.quantidadeDisponivel, restante);
    simulacao.push({ lote, usar });
    restante -= usar;
  }
  const custoTotal = simulacao.reduce((s, { lote, usar }) => s + usar * lote.custoUnitario, 0);

  return (
    <div className="fifo-preview">
      {simulacao.map(({ lote, usar }) => (
        <span key={lote.id} className="fifo-preview__item">
          {fmt(usar)} {unidade} × R${fmt(lote.custoUnitario)}
        </span>
      ))}
      {simulacao.length > 0 && (
        <span className="fifo-preview__total">= {fmtBRL(custoTotal)}</span>
      )}
    </div>
  );
}

// ── Bloco de um insumo no lançamento ──────────────────────────────────────────
function LinhaInsumo({ linha, areaHectares, onChange, onRemove, podRemover, indice, total }) {
  const idCustoManual = useId();
  const unidade = linha.insumo?.unidadeMedida || '';
  const qtdTotal = linha.porHectare && linha.dose && areaHectares
    ? parseFloat((parseFloat(linha.dose) * areaHectares).toFixed(3))
    : parseFloat(linha.quantidade) || 0;

  const estoqueDisponivel = linha.lotes.reduce((s, l) => s + l.quantidadeDisponivel, 0);
  // Só afirma falta de estoque quando a consulta realmente respondeu. Com lotesOk
  // null (ainda consultando) ou false (falhou) não se diz nada sobre o saldo.
  const semEstoque = linha.lotesOk === true && linha.insumo?.id && qtdTotal > 0 && estoqueDisponivel < qtdTotal;
  const estoqueIndisponivelParaConsulta = linha.lotesOk === false && !!linha.insumo?.id;

  // Digitar no nome não é trocar de insumo. Só a escolha de um item do catálogo
  // invalida quantidade, dose e custo — a dose de um adubo não serve para um
  // defensivo. Antes qualquer tecla chegava aqui como "id null" e apagava tudo.
  const handleInsumo = (i, meta) => {
    if (meta?.origem === ORIGEM_DIGITACAO) {
      // O texto deixou de corresponder a um insumo do catálogo: solta o vínculo
      // e o estoque consultado, mas guarda o que o usuário já digitou.
      if (linha.insumo) onChange({ insumo: null, lotes: [], lotesOk: null });
      return;
    }
    const insumo = i?.id ? i : null;
    // Atualização parcial: o pai mescla no estado atual, então a resposta da
    // consulta de lotes não sobrescreve a quantidade digitada nesse meio-tempo.
    onChange({ insumo, lotes: [], lotesOk: null, quantidade: '', dose: '', custoManual: '' });
    if (i?.id) {
      api.getLotesEstoque(i.id)
        .then(lotes => onChange({ lotes: lotes || [], lotesOk: true }))
        .catch(() => onChange({ lotes: [], lotesOk: false }));
    }
  };

  const titulo = total > 1 ? `Insumo ${indice + 1}` : 'Insumo';

  return (
    <div className="linha-insumo">
      <div className="linha-insumo__cab">
        <div className="linha-insumo__titulo">{titulo}</div>
        {podRemover && (
          <button
            type="button"
            onClick={onRemove}
            aria-label={`Remover ${titulo.toLowerCase()} do lançamento`}
            className="item-card-btn item-card-btn--danger"
          >
            Remover
          </button>
        )}
      </div>

      {/* Cada rótulo mora junto do seu campo: com uma linha de rótulos separada,
          em tela de 320px a quantidade quebrava para baixo e ficava sem rótulo. */}
      <FormRow min={150}>
        <Field label="Qual insumo">
          <InsumoAutocomplete value={linha.insumo} onChange={handleInsumo} placeholder="Buscar insumo..." />
        </Field>

        {linha.porHectare ? (
          <Field label={unidade ? `Dose (${unidade}/ha)` : 'Dose por hectare'}>
            <input
              type="number" step="0.001" min="0.001"
              value={linha.dose}
              onChange={e => onChange({ dose: e.target.value })}
              placeholder="Ex: 1"
            />
          </Field>
        ) : (
          <Field label={unidade ? `Quantidade (${unidade})` : 'Quantidade'}>
            <input
              type="number" step="0.001" min="0.001"
              value={linha.quantidade}
              onChange={e => onChange({ quantidade: e.target.value })}
              placeholder="Ex: 600"
            />
          </Field>
        )}
      </FormRow>

      {linha.insumo?.id && areaHectares && (
        <label className="check-inline">
          <input
            type="checkbox"
            checked={linha.porHectare}
            onChange={e => onChange({ porHectare: e.target.checked, quantidade: '', dose: '' })}
          />
          Por hectare ({areaHectares} ha)
          {linha.porHectare && qtdTotal > 0 && (
            <span className="check-inline__total">→ {fmt(qtdTotal)} {unidade} total</span>
          )}
        </label>
      )}

      {linha.lotesOk === true && linha.insumo?.id && qtdTotal > 0 && !semEstoque && (
        <FifoPreview lotes={linha.lotes} quantidade={qtdTotal} unidade={unidade} />
      )}

      {/* Consulta de estoque falhou: a prévia é só informativa e o backend refaz o
          FIFO ao salvar, então nada de pedir custo manual por causa de rede. */}
      {estoqueIndisponivelParaConsulta && (
        <div className="aviso aviso--atencao">
          Não foi possível verificar o estoque agora. O custo será calculado pelo servidor ao salvar.
        </div>
      )}

      {semEstoque && (
        <div className="aviso aviso--erro">
          <div className="login-titulo">
            <Icon nome="alerta" tamanho={14} />
            Estoque insuficiente — {fmt(estoqueDisponivel)} {unidade} disponível, {fmt(qtdTotal)} {unidade} necessário
          </div>
          <p>
            Registrar sem estoque gera custo R$&nbsp;0,00 nos relatórios de produção. Informe o custo total manualmente para manter os relatórios corretos.
          </p>
          <div className="aviso-custo-manual">
            <label htmlFor={idCustoManual}>Custo total (R$):</label>
            <input
              id={idCustoManual}
              type="number" step="0.01" min="0"
              value={linha.custoManual ?? ''}
              onChange={e => onChange({ custoManual: e.target.value })}
              placeholder="Ex: 1500,00"
            />
            {parseFloat(linha.custoManual) > 0 && (
              <span className="aviso-custo-manual__ok">será usado como custo deste lançamento</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Aba custos ────────────────────────────────────────────────────────────────
// Recebe custos já carregados: quem cuida de carregando/erro/vazio é o Estado
// em PainelCustos — antes uma falha de rede ficava em "Carregando..." para sempre.
function AbaCustos({ custos, tipoArea }) {
  const areaEfetiva = custos.tipoArea || tipoArea;
  const estufa = areaEfetiva === 'ESTUFA';
  const rotuloArea = labelPorArea('Custo', areaEfetiva);
  // Sem produção registrada não há unidade: antes assumia SACO, o que mentia
  // para uma estufa que mede em caixas.
  const temProducao = custos.totalProduzido > 0;
  const unidade = custos.unidadeProducao;

  const cards = [
    { label: 'Custo Total', value: fmtBRL(custos.custoTotalBRL),
      dica: 'Soma de todos os custos lançados nesta área nesta safra.' },
    { label: rotuloArea, value: custos.custoPorHectareBRL != null ? fmtBRL(custos.custoPorHectareBRL) : '—',
      dica: estufa
        ? 'Em estufa a metragem é em m², então este valor é o custo da estufa inteira.'
        : 'Custo total dividido pela área plantada nesta safra.' },
  ];
  if (temProducao) {
    cards.push({
      label: labelPorUnidade('Custo', unidade),
      value: custos.custoPorUnidadeBRL != null ? fmtBRL(custos.custoPorUnidadeBRL) : '—',
      dica: 'Quanto custou produzir cada unidade. Compare com o preço de venda para saber a margem.',
    });
    cards.push({ label: 'Produção', value: formatarProducao(custos.totalProduzido, unidade) });
  } else {
    cards.push({ label: 'Produção', value: '—', dica: 'Registre a produção em Produção / Cargas.' });
  }

  return (
    <div>
      {custos.unidadesMistas && (
        <div className="aviso aviso--atencao">
          Produção registrada em unidades diferentes. O custo por unidade usa a de maior
          volume ({labelUnidadePlural(unidade)}).
        </div>
      )}
      <MetricaGrid min={140}>
        {cards.map(m => <Metrica key={m.label} label={m.label} value={m.value} dica={m.dica} />)}
      </MetricaGrid>

      {custos.unidadesMistas && custos.producaoPorUnidade?.length > 0 && (
        <div className="chips-producao">
          {custos.producaoPorUnidade.map(p => (
            <span key={p.unidade}>
              {labelUnidadePlural(p.unidade)}: <strong>{fmt(p.total)}</strong>
            </span>
          ))}
        </div>
      )}

      <p className="texto-ajuda">
        {estufa
          ? 'Custo da estufa e produção são calculados automaticamente ao registrar produção.'
          : 'Custo por hectare e produção são calculados automaticamente ao registrar produção.'}
        {!temProducao && ' Registre a produção para ver o custo por unidade.'}
      </p>
      {custos.custosPorCategoria?.length > 1 && (
        <div className="bloco-relatorio">
          <h3 className="titulo-secao-sm">Gasto por categoria</h3>
          <div className="grafico-centro">
            <DonutChart
              data={custos.custosPorCategoria.map(c => ({
                label: labelCategoria(c.categoria),
                value: c.custoTotalBRL,
              }))}
              centro={fmtBRL(custos.custoTotalBRL)}
              centroSub="total"
            />
          </div>
          <DataTable
            densa
            linhas={custos.custosPorCategoria}
            chaveDe={(c) => c.categoria}
            colunas={[
              { chave: 'categoria', titulo: 'Categoria', papel: 'titulo',
                render: c => labelCategoria(c.categoria) },
              { chave: 'custo', titulo: 'Custo', alinhar: 'direita', papel: 'destaque',
                render: c => fmtBRL(c.custoTotalBRL) },
              // Fica como detalhe rotulado: "32,5%" sozinho embaixo da categoria
              // não diz de que é a porcentagem, e o rótulo resolve em uma linha.
              { chave: 'pct', titulo: '% do total', alinhar: 'direita', render: c => {
                  if (!(custos.custoTotalBRL > 0)) return '—';
                  return `${((c.custoTotalBRL / custos.custoTotalBRL) * 100).toFixed(1)}%`;
                } },
            ]}
          />
        </div>
      )}

      {custos.custosPorInsumo?.length > 0 && (
        <>
          <h3 className="titulo-secao-sm titulo-secao-sm--aperto">Onde o dinheiro foi</h3>
          <DataTable
            densa
            linhas={custos.custosPorInsumo}
            chaveDe={(item) => `${item.nomeInsumo}-${item.categoria}`}
            colunas={[
              { chave: 'nome', titulo: 'Insumo / gasto', papel: 'titulo', render: i => i.nomeInsumo },
              { chave: 'custo', titulo: 'Custo', alinhar: 'direita', papel: 'destaque',
                render: i => fmtBRL(i.custoTotalBRL) },
              // Categoria é autoexplicativa, então vira a linha de contexto do
              // cartão em vez de gastar uma linha rotulada.
              { chave: 'categoria', titulo: 'Categoria', ocultarSeVazia: true,
                valor: i => i.categoria, papel: 'subtitulo',
                render: i => labelCategoria(i.categoria) },
              { chave: 'qtd', titulo: 'Quantidade', alinhar: 'direita',
                render: i => `${fmt(i.quantidadeTotal)} ${i.unidadeMedida || ''}` },
              { chave: 'pct', titulo: '% do total', alinhar: 'direita', render: i => {
                  if (!(custos.custoTotalBRL > 0)) return '—';
                  const pct = (i.custoTotalBRL / custos.custoTotalBRL) * 100;
                  return (
                    <span className="barra-pct">
                      {pct.toFixed(1)}%
                      <span className="barra-pct__trilha">
                        <span className="barra-pct__fill" style={{ width: `${Math.min(pct, 100)}%` }} />
                      </span>
                    </span>
                  );
                } },
            ]}
          />
        </>
      )}
    </div>
  );
}

// ── Grupo de aplicação (vários insumos do mesmo lote) ─────────────────────────
function GrupoAplicacao({ grupo, onEditar, onExcluir, onEditarGrupo, onExcluirGrupo }) {
  const custoGrupo = grupo.itens.reduce((s, a) => s + (a.custoTotal || 0), 0);
  // Ações de lançamento inteiro só fazem sentido quando o lote tem mais de um
  // insumo. Lançamento de um item só já é totalmente coberto pelos botões da
  // própria linha (o grupo é a linha).
  const temGrupo = !!grupo.loteId && grupo.itens.length > 1;
  return (
    <div className="grupo-aplicacao">
      <div className="grupo-aplicacao__cab">
        <div className="grupo-aplicacao__meta">
          <span className="grupo-aplicacao__data">
            {new Date(grupo.dataAplicacao + 'T00:00:00').toLocaleDateString('pt-BR')}
          </span>
          <span className="grupo-aplicacao__tipo">{grupo.tipoAplicacao}</span>
          {grupo.observacao && <span className="grupo-aplicacao__obs">{grupo.observacao}</span>}
        </div>
        <span className="grupo-aplicacao__valor">{fmtBRL(custoGrupo)}</span>
      </div>
      {temGrupo && (
        <div className="grupo-aplicacao__acoes">
          <span className="grupo-aplicacao__acoes-rotulo">Lançamento inteiro:</span>
          <button type="button" className="item-card-btn" onClick={() => onEditarGrupo(grupo)}>
            Editar data/tipo
          </button>
          <button
            type="button"
            className="item-card-btn item-card-btn--danger"
            onClick={() => onExcluirGrupo(grupo)}
          >
            Excluir tudo
          </button>
        </div>
      )}
      {/* Cada item do lote. O layout usa classes em vez de flex inline com
          minWidth fixo: no celular os três blocos de 80 px quebravam em linhas
          separadas e o nome do insumo ficava órfão do valor dele. */}
      {grupo.itens.map((a) => (
        <div key={a.id} className="lote-item">
          <span className="lote-item__nome">
            {a.insumo?.nome || a.descricaoCusto || a.tipoAplicacao}
            {a.categoria && (
              <span className="lote-item__tag">{labelCategoria(a.categoria)}</span>
            )}
            {!a.consumiuEstoque && (
              <span className="lote-item__sem-estoque">
                sem estoque
              </span>
            )}
          </span>
          <span className="lote-item__qtd">{fmt(a.quantidade)} {a.insumo?.unidadeMedida}</span>
          <span className="lote-item__valor">{fmtBRL(a.custoTotal)}</span>
          <div className="lote-item__acoes">
            <button type="button" className="item-card-btn" onClick={() => onEditar(a)}>
              Editar
            </button>
            <button
              type="button"
              className="item-card-btn item-card-btn--danger"
              onClick={() => onExcluir(a.id, !!a.consumiuEstoque)}
            >
              Excluir
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function custoFifo(lotes, quantidade) {
  let restante = quantidade;
  let custo = 0;
  for (const lote of lotes) {
    if (restante <= 0) break;
    const usar = Math.min(lote.quantidadeDisponivel, restante);
    custo += usar * (lote.custoUnitario || 0);
    restante -= usar;
  }
  return custo;
}

// ── Correção de um lançamento já gravado ──────────────────────────────────────
function FormEditarLancamento({ lancamento, onCancel, onSalvou, onErro }) {
  const usouEstoque = !!lancamento.consumiuEstoque && !!lancamento.insumo;
  const unidade = lancamento.insumo?.unidadeMedida || '';
  const qtdOriginal = lancamento.quantidade ?? 0;
  const valorOriginal = lancamento.custoTotal ?? 0;

  const [quantidade, setQuantidade] = useState(qtdOriginal !== 0 ? String(qtdOriginal) : '');
  const [custoTotal, setCustoTotal] = useState(valorOriginal !== 0 ? String(valorOriginal) : '');
  const [descricao, setDescricao] = useState(lancamento.descricaoCusto || '');
  const [lotes, setLotes] = useState([]);
  const [lotesOk, setLotesOk] = useState(usouEstoque ? null : true);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (!usouEstoque || !lancamento.insumo?.id) return;
    let ativo = true;
    api.getLotesEstoque(lancamento.insumo.id)
      .then(list => { if (!ativo) return; setLotes(list || []); setLotesOk(true); })
      .catch(() => { if (!ativo) return; setLotes([]); setLotesOk(false); });
    return () => { ativo = false; };
  }, [usouEstoque, lancamento.insumo?.id]);

  const qtdNova = parseFloat(quantidade);
  const valorNovo = parseFloat(custoTotal);
  const qtdValida = Number.isFinite(qtdNova) && qtdNova > 0;
  const valorValido = Number.isFinite(valorNovo) && valorNovo > 0;
  const delta = qtdValida ? qtdNova - qtdOriginal : 0;
  const extra = delta > 0 ? delta : 0;
  const estoqueDisponivel = lotes.reduce((s, l) => s + (l.quantidadeDisponivel || 0), 0);
  const semEstoque = usouEstoque && lotesOk === true && extra > 0 && estoqueDisponivel + 0.0001 < extra;

  const custoPrevisto = usouEstoque
    ? (delta > 0
        ? valorOriginal + custoFifo(lotes, extra)
        : (qtdValida ? qtdNova * (lancamento.custoUnitario || 0) : valorOriginal))
    : (valorValido ? valorNovo : valorOriginal);

  const mudouQtd = qtdValida && Math.abs(delta) >= 0.0001;
  const mudouValor = valorValido && Math.abs(valorNovo - valorOriginal) >= 0.01;
  const mudouDescricao = descricao.trim() !== (lancamento.descricaoCusto || '');
  const temMudanca = usouEstoque ? mudouQtd : (mudouValor || mudouQtd || mudouDescricao);
  const esperandoEstoque = usouEstoque && extra > 0 && lotesOk === null;
  const podeSalvar = temMudanca && !salvando && !semEstoque && !esperandoEstoque
    && (usouEstoque ? qtdValida : (mudouValor ? valorValido : true));

  const salvar = async (e) => {
    e.preventDefault();
    if (!podeSalvar) return;
    setSalvando(true);
    try {
      const body = usouEstoque
        ? { quantidade: qtdNova }
        : {
            ...(mudouQtd ? { quantidade: qtdNova } : {}),
            ...(mudouValor ? { custoTotal: valorNovo } : {}),
            ...(mudouDescricao ? { descricaoCusto: descricao.trim() } : {}),
          };
      await api.editarCusto(lancamento.id, body);
      onSalvou();
    } catch (err) {
      onErro(err.message);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <form onSubmit={salvar}>
      {usouEstoque ? (
        <>
          <p className="form-custo-hint form-custo-hint--topo">
            A quantidade a mais sai do estoque. A quantidade a menos volta para o estoque.
            O valor é recalculado sozinho.
          </p>
          <Field
            label={`Quantidade${unidade ? ` (${unidade})` : ''}`}
            obrigatorio
            hint={`${fmt(qtdOriginal)} ${unidade} lançados`.trim()}
            erro={semEstoque
              ? `Estoque insuficiente — ${fmt(estoqueDisponivel)} ${unidade} disponível, ${fmt(extra)} ${unidade} a mais`
              : undefined}
          >
            <input
              type="number"
              step="0.001"
              min="0.001"
              value={quantidade}
              onChange={e => setQuantidade(e.target.value)}
              required
            />
          </Field>
          {extra > 0 && lotesOk === true && !semEstoque && (
            <FifoPreview lotes={lotes} quantidade={extra} unidade={unidade} />
          )}
          {delta < 0 && qtdValida && (
            <p className="form-custo-hint">
              Voltam {fmt(-delta)} {unidade} ao estoque.
            </p>
          )}
          {lotesOk === false && extra > 0 && (
            <div className="aviso aviso--atencao">
              Não foi possível verificar o estoque agora. O servidor confirma o saldo ao salvar.
            </div>
          )}
        </>
      ) : (
        <>
          <p className="form-custo-hint form-custo-hint--topo">
            Este lançamento não mexeu no estoque. Corrija o valor
            {lancamento.insumo ? ' (e a quantidade, se estiver errada).' : '.'}
          </p>
          {!lancamento.insumo && (
            <Field label="Descrição">
              <input
                type="text"
                value={descricao}
                onChange={e => setDescricao(e.target.value)}
                maxLength={200}
              />
            </Field>
          )}
          <FormRow>
            {lancamento.insumo && (
              <Field label={`Quantidade${unidade ? ` (${unidade})` : ''}`}>
                <input
                  type="number"
                  step="0.001"
                  min="0.001"
                  value={quantidade}
                  onChange={e => setQuantidade(e.target.value)}
                />
              </Field>
            )}
            <Field label="Custo total (R$)" obrigatorio>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={custoTotal}
                onChange={e => setCustoTotal(e.target.value)}
                required
              />
            </Field>
          </FormRow>
        </>
      )}

      {temMudanca && Number.isFinite(custoPrevisto) && (
        <p className="form-editar-custo-resumo">
          Novo custo: <strong>{fmtBRL(custoPrevisto)}</strong>
          {usouEstoque && valorOriginal !== custoPrevisto && (
            <span> · era {fmtBRL(valorOriginal)}</span>
          )}
        </p>
      )}

      <div className="actions modal-rodape">
        <button type="button" className="btn-secondary" disabled={salvando} onClick={onCancel}>
          Cancelar
        </button>
        <button type="submit" className="btn-primary" disabled={!podeSalvar}>
          {salvando ? 'Salvando...' : 'Salvar correção'}
        </button>
      </div>
    </form>
  );
}

// lotesOk: null = estoque ainda não consultado, true = consulta respondeu,
// false = a consulta falhou. Sem essa distinção, uma falha de rede virava
// "estoque insuficiente" e o formulário pedia custo manual — dado errado.
const linhaVazia = () => ({ id: Date.now(), insumo: null, lotes: [], lotesOk: null, quantidade: '', dose: '', porHectare: false, custoManual: '' });

// ── Formulário de lançamento ──────────────────────────────────────────────────
const CABECALHO_VAZIO = {
  dataAplicacao: hoje(),
  tipoAplicacao: 'PLANTIO',
  observacao: '',
  usarEstoque: true,
  // Campos do lançamento sem uso de estoque
  categoria: categoriaPadraoPara('PLANTIO'),
  insumoAvulso: null,      // { nome, id, unidadeMedida } — id != null quando é do catálogo
  quantidadeAvulsa: '',
  custoAvulso: '',
};

export function FormCusto({ sl, onMsg, onSalvou }) {
  const [cabecalho, setCabecalho] = useState({ ...CABECALHO_VAZIO, dataAplicacao: hoje() });
  const [linhas, setLinhas] = useState([linhaVazia()]);
  const [salvando, setSalvando] = useState(false);
  // Enquanto o usuário não escolher categoria à mão, ela acompanha o tipo de
  // custo — quem lança arrendamento não deveria ter que dizer "Serviço" também.
  const [categoriaTocada, setCategoriaTocada] = useState(false);

  const areaHectares = sl.areaHectares;
  const usarEstoque = cabecalho.usarEstoque;
  const insumoAvulso = cabecalho.insumoAvulso;
  const insumoVinculado = insumoAvulso?.id ? insumoAvulso : null;

  const trocarTipo = (v) => setCabecalho(c => ({
    ...c,
    tipoAplicacao: v,
    categoria: categoriaTocada ? c.categoria : categoriaPadraoPara(v),
  }));

  const trocarCategoria = (v) => {
    setCategoriaTocada(true);
    // Trocar de categoria invalida o insumo já escolhido: ele pertence à antiga.
    setCabecalho(c => ({ ...c, categoria: v, insumoAvulso: null, quantidadeAvulsa: '' }));
  };

  const limparCabecalho = () => setCabecalho(c => ({
    ...CABECALHO_VAZIO,
    dataAplicacao: hoje(),
    tipoAplicacao: c.tipoAplicacao,
    categoria: c.categoria,
    usarEstoque: c.usarEstoque,
  }));

  const atualizarLinha = (id, dados) => setLinhas(ls => ls.map(l => l.id === id ? { ...l, ...dados } : l));
  const removerLinha = (id) => setLinhas(ls => ls.filter(l => l.id !== id));
  const adicionarLinha = () => setLinhas(ls => [...ls, linhaVazia()]);

  const salvar = async () => {
    if (!usarEstoque) {
      const valor = parseFloat(cabecalho.custoAvulso);
      const descricao = (insumoAvulso?.nome || '').trim();

      if (!descricao) { onMsg('error', 'Informe o que foi o gasto.'); return; }
      if (!cabecalho.categoria) { onMsg('error', 'Escolha a categoria do gasto.'); return; }
      if (!valor || valor <= 0) { onMsg('error', 'Informe o valor do custo.'); return; }

      const qtd = parseFloat(cabecalho.quantidadeAvulsa);

      setSalvando(true);
      try {
        await api.registrarCusto({
          safraLavouraId: sl.id,
          // Quando o gasto é de um insumo do catálogo, o lançamento fica ligado a
          // ele (aparece no "onde o dinheiro foi" com nome e unidade) mas sem
          // consumir estoque — é o caso de comprar e aplicar no mesmo dia.
          insumoId: insumoVinculado?.id || null,
          descricaoCusto: insumoVinculado ? null : descricao,
          categoria: cabecalho.categoria,
          dataAplicacao: cabecalho.dataAplicacao,
          quantidade: qtd > 0 ? qtd : 1,
          tipoAplicacao: cabecalho.tipoAplicacao,
          observacao: cabecalho.observacao || null,
          custoManual: valor,
          usarEstoque: false,
        });
        limparCabecalho();
        onMsg('success', 'Custo lançado!');
        if (onSalvou) onSalvou();
      } catch (err) { onMsg('error', err.message); }
      finally { setSalvando(false); }
      return;
    }

    // Modo insumo: validação normal
    const validas = linhas.filter(l => {
      const qtd = l.porHectare && l.dose && areaHectares
        ? parseFloat(l.dose) * areaHectares
        : parseFloat(l.quantidade);
      return l.insumo?.id && qtd > 0;
    });

    if (validas.length === 0) { onMsg('error', 'Preencha ao menos um insumo com quantidade.'); return; }

    setSalvando(true);
    try {
      await api.registrarCustoLote(validas.map(l => {
        const qtd = l.porHectare && l.dose && areaHectares
          ? parseFloat((parseFloat(l.dose) * areaHectares).toFixed(3))
          : parseFloat(l.quantidade);
        const custoManual = parseFloat(l.custoManual) > 0 ? parseFloat(l.custoManual) : null;
        return {
          safraLavouraId: sl.id,
          insumoId: l.insumo.id,
          descricaoCusto: null,
          dataAplicacao: cabecalho.dataAplicacao,
          quantidade: qtd,
          tipoAplicacao: cabecalho.tipoAplicacao,
          observacao: cabecalho.observacao || null,
          custoManual,
          usarEstoque: true,
        };
      }));
      setLinhas([linhaVazia()]);
      limparCabecalho();
      onMsg('success', `${validas.length} custo(s) lançado(s)!`);
      if (onSalvou) onSalvou();
    } catch (err) {
      onMsg('error', err.message);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="form-custo">
          {/* Cabeçalho */}
          <div className="form-custo-cab">
            <div className="form-custo-cab__campo">
              <label>Data</label>
              <input type="date" value={cabecalho.dataAplicacao} onChange={e => setCabecalho(c => ({ ...c, dataAplicacao: e.target.value }))} />
            </div>
            <div className="form-custo-cab__campo">
              <label>Tipo</label>
              <Select value={cabecalho.tipoAplicacao} onChange={trocarTipo} options={TIPOS_CUSTO.map(t => ({ value: t.value, label: t.label }))} />
            </div>
            <div className="form-custo-cab__campo form-custo-cab__campo--largo">
              <label>Observação</label>
              <input value={cabecalho.observacao} onChange={e => setCabecalho(c => ({ ...c, observacao: e.target.value }))} placeholder="Opcional" />
            </div>
          </div>

          <label className="check-inline check-inline--form">
            <input
              type="checkbox"
              checked={cabecalho.usarEstoque}
              onChange={e => setCabecalho(c => ({ ...c, usarEstoque: e.target.checked }))}
            />
            Usar estoque de insumo
            <span className="check-inline__total">
              {usarEstoque ? '(desconta do saldo)' : '(valor informado direto)'}
            </span>
          </label>

          {/* Container de lançamento: alterna entre insumo e (!usarEstoque) */}
          {(!usarEstoque) ? (
            <div className="form-custo-caixa">
              <FormRow min={160}>
                {/* A categoria é o que faz este gasto aparecer no relatório por
                    categoria. Sem ela, arrendamento e diária ficavam de fora. */}
                <Field label="Categoria" obrigatorio hint="Como este gasto entra no relatório">
                  <Select
                    value={cabecalho.categoria}
                    onChange={trocarCategoria}
                    options={CATEGORIAS}
                    placeholder="Selecione"
                    required
                  />
                </Field>

                <Field
                  label="Descrição do gasto"
                  obrigatorio
                  hint={insumoVinculado
                    ? `Ligado ao insumo "${insumoVinculado.nome}" do seu catálogo — o estoque não será descontado.`
                    : 'Digite livremente, ou escolha um insumo do catálogo nas sugestões.'}
                >
                  <InsumoAutocomplete
                    value={insumoAvulso}
                    onChange={(i, meta) => setCabecalho(c => {
                      const digitando = meta?.origem === ORIGEM_DIGITACAO;
                      // Só a troca de insumo do catálogo invalida a quantidade,
                      // porque a unidade de medida vem dele. Ajustar uma letra do
                      // texto não pode apagar o número já digitado.
                      const trocouInsumo = !digitando && i?.id !== c.insumoAvulso?.id;
                      return {
                        ...c,
                        insumoAvulso: i?.nome ? i : null,
                        quantidadeAvulsa: trocouInsumo ? '' : c.quantidadeAvulsa,
                      };
                    })}
                    categoria={cabecalho.categoria}
                    placeholder="Ex: Arrendamento lote 3, Diária tratorista"
                  />
                </Field>
              </FormRow>

              <FormRow min={140}>
                {insumoVinculado && (
                  <Field
                    label={`Quantidade (${insumoVinculado.unidadeMedida || 'un'})`}
                    hint="Opcional — usada só para mostrar o custo unitário"
                  >
                    <input
                      type="number" step="0.001" min="0"
                      value={cabecalho.quantidadeAvulsa}
                      onChange={e => setCabecalho(c => ({ ...c, quantidadeAvulsa: e.target.value }))}
                      placeholder="Ex: 200"
                    />
                  </Field>
                )}
                <Field label="Valor total (R$)" obrigatorio>
                  <input
                    type="number" step="0.01" min="0.01"
                    value={cabecalho.custoAvulso}
                    onChange={e => setCabecalho(c => ({ ...c, custoAvulso: e.target.value }))}
                    placeholder="Ex: 50000"
                  />
                </Field>
              </FormRow>

              <p className="form-custo-hint">
                Este lançamento não consome estoque de insumo: o valor informado entra
                direto no resultado da safra.
              </p>
            </div>
          ) : (
            <div className="form-custo-insumos">
            {linhas.map((l, idx) => (
              <LinhaInsumo
                key={l.id}
                linha={l}
                areaHectares={areaHectares}
                onChange={dados => atualizarLinha(l.id, dados)}
                onRemove={() => removerLinha(l.id)}
                podRemover={linhas.length > 1}
                indice={idx}
                total={linhas.length}
              />
            ))}

            <button
              type="button"
              className="btn-texto"
              onClick={adicionarLinha}
            >
              Adicionar outro insumo
            </button>
          </div>
          )}

          <div className="actions-direita">
            <button className="btn-primary" onClick={salvar} disabled={salvando}>
              {salvando ? 'Salvando...' : 'Lançar custo'}
            </button>
          </div>
    </div>
  );
}

// ── Histórico de custos do talhão ─────────────────────────────────────────────
// ── Correção do cabeçalho de um lançamento inteiro (todas as linhas do lote) ──
function FormEditarLancamentoGrupo({ grupo, onCancel, onSalvou, onErro }) {
  const [data, setData] = useState((grupo.dataAplicacao || '').slice(0, 10));
  const [tipo, setTipo] = useState(grupo.tipoAplicacao || 'OUTRO');
  const [observacao, setObservacao] = useState(grupo.observacao || '');
  const [salvando, setSalvando] = useState(false);

  const salvar = async () => {
    if (!data) { onErro('Informe a data do lançamento.'); return; }
    setSalvando(true);
    try {
      await api.editarLancamento(grupo.loteId, {
        dataAplicacao: data,
        tipoAplicacao: tipo,
        // Envia string vazia para limpar a observação (o backend trata em branco como null).
        observacao: observacao ?? '',
      });
      onSalvou();
    } catch (err) {
      onErro(err.message);
      setSalvando(false);
    }
  };

  return (
    <div>
      <p className="secao-sub secao-sub--bloco">
        Corrige a data e o tipo de todos os {grupo.itens.length} insumos deste lançamento.
        A quantidade e o valor de cada insumo continuam sendo ajustados no botão
        "Editar" de cada linha.
      </p>
      <FormRow>
        <Field label="Data do lançamento" obrigatorio>
          <input type="date" value={data} onChange={(e) => setData(e.target.value)} required />
        </Field>
        <Field label="Tipo do custo" obrigatorio>
          <Select
            value={tipo}
            onChange={(v) => setTipo(v)}
            options={TIPOS_CUSTO}
          />
        </Field>
      </FormRow>
      <Field label="Observação (opcional)">
        <input
          value={observacao}
          onChange={(e) => setObservacao(e.target.value)}
          placeholder="Anotação sobre este lançamento"
          maxLength={255}
        />
      </Field>
      <div className="actions modal-rodape">
        <button type="button" className="btn-secondary" onClick={onCancel}>Cancelar</button>
        <button type="button" className="btn-primary" onClick={salvar} disabled={salvando}>
          {salvando ? 'Salvando...' : 'Salvar'}
        </button>
      </div>
    </div>
  );
}

export function HistoricoCustos({ sl, onMsg, onSalvou, refreshKey }) {
  const [aplicacoes, setAplicacoes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [excluindo, setExcluindo] = useState(null);
  const [editando, setEditando] = useState(null);
  const [editandoGrupo, setEditandoGrupo] = useState(null);
  const [excluindoGrupo, setExcluindoGrupo] = useState(null);

  const carregar = useCallback(() => {
    setCarregando(true);
    setErro(null);
    api.getCustos(sl.id)
      .then(list => { setAplicacoes(list || []); setCarregando(false); })
      // Lista vazia e lista que não chegou são coisas diferentes: sem isso a tela
      // dizia "nenhum custo lançado" para uma área cheia de gastos.
      .catch(() => {
        setAplicacoes([]);
        setErro('Sem conexão com o servidor. Os lançamentos desta área não foram carregados.');
        setCarregando(false);
      });
  }, [sl.id]);

  useEffect(() => { carregar(); }, [carregar, refreshKey]);

  // Antes eram dois window.confirm em sequência — o segundo pegava o usuário de
  // surpresa e a resposta errada mexia no estoque sem querer. Agora é uma única
  // decisão explícita, com a devolução ao estoque como escolha visível.
  const excluir = (id, usouEstoque) => setExcluindo({ id, usouEstoque, devolver: usouEstoque });

  const confirmarExclusao = async () => {
    const { id, devolver } = excluindo;
    setExcluindo(null);
    try {
      await api.excluirCusto(id, devolver);
      onMsg('success', 'Lançamento excluído.');
      onSalvou?.();
      carregar();
    } catch (err) { onMsg('error', err.message); }
  };

  // Excluir o lançamento inteiro: um grupo pode ter insumos que consumiram
  // estoque; a devolução aparece como opção se qualquer linha tiver consumido.
  const excluirGrupo = (grupo) => {
    const algumComEstoque = grupo.itens.some((a) => a.consumiuEstoque);
    setExcluindoGrupo({ grupo, temEstoque: algumComEstoque, devolver: algumComEstoque });
  };

  const confirmarExclusaoGrupo = async () => {
    const { grupo, devolver } = excluindoGrupo;
    setExcluindoGrupo(null);
    try {
      await api.excluirLancamento(grupo.loteId, devolver);
      onMsg('success', 'Lançamento excluído.');
      onSalvou?.();
      carregar();
    } catch (err) { onMsg('error', err.message); }
  };

  const agrupar = (lista) => {
    const grupos = [];
    const chaves = {};
    lista.forEach(a => {
      const chave = a.loteId ?? `solo-${a.id}`;
      if (!chaves[chave]) {
        chaves[chave] = { chave, loteId: a.loteId ?? null, dataAplicacao: a.dataAplicacao, tipoAplicacao: a.tipoAplicacao, observacao: a.observacao, itens: [] };
        grupos.push(chaves[chave]);
      }
      chaves[chave].itens.push(a);
    });
    return grupos;
  };

  return (
    <div>
      {editando && (
        <Modal
          titulo={`Corrigir — ${editando.insumo?.nome || editando.descricaoCusto || 'lançamento'}`}
          onClose={() => setEditando(null)}
          largura={480}
        >
          <FormEditarLancamento
            lancamento={editando}
            onCancel={() => setEditando(null)}
            onSalvou={() => {
              setEditando(null);
              onMsg('success', 'Lançamento atualizado.');
              onSalvou?.();
              carregar();
            }}
            onErro={msg => onMsg('error', msg)}
          />
        </Modal>
      )}

      {editandoGrupo && (
        <Modal
          titulo="Corrigir lançamento"
          onClose={() => setEditandoGrupo(null)}
          largura={480}
        >
          <FormEditarLancamentoGrupo
            grupo={editandoGrupo}
            onCancel={() => setEditandoGrupo(null)}
            onSalvou={() => {
              setEditandoGrupo(null);
              onMsg('success', 'Lançamento atualizado.');
              onSalvou?.();
              carregar();
            }}
            onErro={msg => onMsg('error', msg)}
          />
        </Modal>
      )}

      {excluindoGrupo && (
        <Modal titulo="Excluir lançamento inteiro" onClose={() => setExcluindoGrupo(null)} largura={440}>
          <p>
            Todos os {excluindoGrupo.grupo.itens.length} insumos deste lançamento serão
            removidos do total da área de cultivo.
          </p>
          {excluindoGrupo.temEstoque && (
            <label className="check-devolver">
              <input
                type="checkbox"
                checked={excluindoGrupo.devolver}
                onChange={e => setExcluindoGrupo(x => ({ ...x, devolver: e.target.checked }))}
              />
              <span>
                <strong>Devolver as quantidades ao estoque de insumo</strong>
                <span className="secao-sub secao-sub--bloco">
                  Marque se os insumos não foram realmente aplicados. Desmarque se
                  foram usados e você está apenas corrigindo o lançamento.
                </span>
              </span>
            </label>
          )}
          <div className="actions modal-rodape">
            <button type="button" className="btn-secondary" onClick={() => setExcluindoGrupo(null)}>Cancelar</button>
            <button type="button" className="btn-danger" onClick={confirmarExclusaoGrupo}>Excluir tudo</button>
          </div>
        </Modal>
      )}

      {excluindo && (
        <Modal titulo="Excluir lançamento" onClose={() => setExcluindo(null)} largura={440}>
          <p>
            Este custo será removido do total da área de cultivo.
          </p>
          {excluindo.usouEstoque && (
            <label className="check-devolver">
              <input
                type="checkbox"
                checked={excluindo.devolver}
                onChange={e => setExcluindo(x => ({ ...x, devolver: e.target.checked }))}
              />
              <span>
                <strong>Devolver a quantidade ao estoque de insumo</strong>
                <span className="secao-sub secao-sub--bloco">
                  Marque se o insumo não foi realmente aplicado. Desmarque se ele foi
                  usado e você está apenas corrigindo o lançamento.
                </span>
              </span>
            </label>
          )}
          <div className="actions modal-rodape">
            <button type="button" className="btn-secondary" onClick={() => setExcluindo(null)}>Cancelar</button>
            <button type="button" className="btn-danger" onClick={confirmarExclusao}>Excluir</button>
          </div>
        </Modal>
      )}

      <Estado
        carregando={carregando}
        erro={erro}
        vazio={aplicacoes.length === 0}
        vazioProps={{
          icone: 'dinheiro',
          titulo: 'Nenhum custo lançado',
          descricao: 'Use a aba Lançar para registrar insumos aplicados, mão de obra e serviços desta área.',
        }}
        aoTentarNovamente={carregar}
        itens={3}
      >
        <>
          {agrupar(aplicacoes).map(g => (
            <GrupoAplicacao
              key={g.chave}
              grupo={g}
              onEditar={setEditando}
              onExcluir={excluir}
              onEditarGrupo={setEditandoGrupo}
              onExcluirGrupo={excluirGrupo}
            />
          ))}
          <div className="lista-custos-total">
            Total: {fmtBRL(aplicacoes.reduce((s, a) => s + (a.custoTotal || 0), 0))}
          </div>
        </>
      </Estado>
    </div>
  );
}

export function PainelCustos({ sl, refreshKey }) {
  const [custos, setCustos] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [tentativa, setTentativa] = useState(0);

  useEffect(() => {
    let ativo = true;
    setCustos(null);
    setErro(null);
    setCarregando(true);
    api.getCustosSafraLavoura(sl.id)
      .then(d => { if (!ativo) return; setCustos(d); setCarregando(false); })
      // Falha de rede não pode virar "nenhum custo": o produtor concluiria que
      // perdeu os lançamentos. Mostra o erro e oferece nova tentativa.
      .catch(() => {
        if (!ativo) return;
        setErro('Sem conexão com o servidor. Os custos desta área não foram carregados.');
        setCarregando(false);
      });
    return () => { ativo = false; };
  }, [sl.id, refreshKey, tentativa]);

  return (
    <Estado
      carregando={carregando}
      erro={erro}
      vazio={!custos}
      vazioProps={{ icone: 'dinheiro', titulo: 'Sem resumo de custos', descricao: 'Lance um custo nesta área para ver o resumo.' }}
      aoTentarNovamente={() => setTentativa(t => t + 1)}
      skeleton="card"
      itens={4}
    >
      <AbaCustos custos={custos} tipoArea={sl.lavoura?.tipoArea} />
    </Estado>
  );
}
